
import React from 'react';
import { Brain, Globe, Bot, GitBranch, ChevronRight, Check, FileInput, Webhook, PenLine, Workflow, Sparkles } from 'lucide-react';
import CTAButton from '../CTAButton';
import SectionLayout from '../SectionLayout';
import { SectionContainer } from '../section/SectionContainer';
import FeatureVideo from './FeatureVideo';

const IntelliFormsSection = () => {
  const features = [{
    icon: <PenLine className="h-5 w-5" />,
    title: "AI-Powered Response Generation",
    description: "Turn form inputs into structured, AI-enhanced text outputs."
  }, {
    icon: <Webhook className="h-5 w-5" />,
    title: "Webhook Integrations",
    description: "Send collected form data to CRMs, databases, or automation tools."
  }, {
    icon: <Bot className="h-5 w-5" />,
    title: "No-Code Form Builder",
    description: "Create forms without coding or technical expertise."
  }, {
    icon: <Workflow className="h-5 w-5" />,
    title: "Seamless Workflow Automation",
    description: "Trigger external actions via webhook-based integrations."
  }];
  
  const useCases = [{
    icon: <Bot className="h-7 w-7" />,
    title: "AI-Enhanced Content Generation",
    description: "Use AI to transform structured form inputs into written content, reports, or formatted text outputs.",
    points: [
      "Generate AI-written summaries based on form responses.",
      "Automatically format responses into structured text or templates.",
      "Create blog content, product descriptions, or other AI-enhanced text outputs."
    ]
  }, {
    icon: <FileInput className="h-7 w-7" />,
    title: "Data Collection & Automated Processing",
    description: "Streamline form-based data collection and send structured responses via webhooks.",
    points: [
      "Collect and store user input efficiently.",
      "Send form data to CRMs, Google Sheets, or internal tools.",
      "Ensure structured data processing without manual intervention."
    ]
  }, {
    icon: <GitBranch className="h-7 w-7" />,
    title: "Customer & Business Workflows",
    description: "Use AI-powered forms to collect inquiries, service requests, or internal business data.",
    points: [
      "Capture customer service requests and forward structured outputs.",
      "Automate booking confirmations and service request responses.",
      "Trigger webhook-based workflows based on form submissions."
    ]
  }];

  return (
    <SectionLayout
      backgroundColor="bg-[#f7faff]"
      paddingY="py-16 md:py-24"
      className="relative"
    >
      <SectionContainer containerWidth="wide" className="relative z-10">
        {/* Header Section */}
        <div className="max-w-6xl mx-auto mb-16 md:mb-20 px-4">
          <div className="relative">
            {/* Background decorative element */}
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-accent/30 rounded-full blur-3xl -z-10"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#003a70]/10 rounded-full blur-3xl -z-10"></div>
            
            {/* Content wrapper with gradient border */}
            <div className="relative border border-gradient-to-r from-accent to-[#003a70]/20 rounded-2xl bg-white/70 backdrop-blur-sm p-8 md:p-10 shadow-xl">
              {/* Badge with animated sparkle effect */}
              <div className="flex justify-center mb-6">
                <div className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-accent to-accent/60 text-[#003a70] text-sm font-medium shadow-sm transform hover:scale-105 transition-all duration-300">
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  <span className="relative z-10 font-semibold tracking-wide">INTELLIFORMS</span>
                </div>
              </div>
              
              {/* Two-part heading with underline accent */}
              <h2 className="text-center">
                <span className="block text-3xl md:text-4xl lg:text-5xl font-bold text-[#003a70] mb-3 leading-tight">
                  AI-Powered Forms That
                </span>
                <span className="relative inline-block text-2xl md:text-3xl lg:text-4xl font-bold text-[#003a70]/80">
                  Generate Structured Outputs
                  <span className="absolute -bottom-2 left-0 w-full h-1.5 bg-accent rounded-full transform -skew-x-6"></span>
                </span>
              </h2>
              
              {/* Enhanced description */}
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mt-8 text-center leading-relaxed">
                Collect data, process responses with AI, and send structured outputs to your tools using webhook 
                integrations—no manual work required.
              </p>
            </div>
          </div>
        </div>

        {/* Video Section with new embed code */}
        <FeatureVideo 
          arcadeEmbed='<!--ARCADE EMBED START--><div style="position: relative; padding-bottom: calc(47.447916666666664% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/eRVuemrjVAkoOIObsj2s?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="Crafting Intelligent Forms with AskSpot" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" ></iframe></div><!--ARCADE EMBED END-->'
        />

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 hover:border-accent"
            >
              <div className="bg-accent p-3 rounded-lg inline-block mb-4">
                <div className="text-[#003a70]">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-lg font-semibold text-[#003a70] mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Use Cases Section */}
        <div className="mb-12">
          <h3 className="text-xl md:text-2xl font-semibold text-[#003a70] text-center mb-10">
            How Businesses Use IntelliForms
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-accent group"
              >
                <div className="bg-accent p-3 rounded-lg inline-block mb-4 group-hover:bg-[#003a70] transition-colors duration-300">
                  <div className="text-[#003a70] group-hover:text-white transition-colors duration-300">
                    {useCase.icon}
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-[#003a70] mb-3">{useCase.title}</h4>
                <p className="text-gray-600 mb-5">{useCase.description}</p>
                <ul className="space-y-2">
                  {useCase.points.map((point, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-[#003a70] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <CTAButton 
            variant="primary" 
            className="group text-lg px-8 py-4"
          >
            Try IntelliForms Now
            <ChevronRight className="inline-block ml-1 group-hover:translate-x-1 transition-transform" />
          </CTAButton>
        </div>
      </SectionContainer>
    </SectionLayout>
  );
};

export default IntelliFormsSection;
